"use client";

declare global {
  interface Window {
    fbq?: (
      command: "track" | "trackCustom" | "init" | "consent",
      eventNameOrConsent: string,
      params?: Record<string, unknown>,
      options?: Record<string, unknown>,
    ) => void;
    _fbq?: Window["fbq"];
  }
}

function hasMetaAdConsent(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const storedConsent = window.localStorage.getItem("cookieConsent");
  if (!storedConsent) {
    return false;
  }

  try {
    const parsed = JSON.parse(storedConsent) as { adConsent?: boolean };
    return parsed.adConsent === true;
  } catch {
    return false;
  }
}

function canTrackMetaBrowser(): boolean {
  return typeof window !== "undefined" && !!window.fbq && hasMetaAdConsent();
}

export function trackMetaBrowserPageView(): boolean {
  if (!canTrackMetaBrowser()) {
    return false;
  }

  window.fbq!("track", "PageView");
  return true;
}

function sleepWithAbort(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);

    function onAbort() {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function retryUntilTracked(
  track: () => boolean,
  options?: {
    intervalMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
  },
): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  const intervalMs = options?.intervalMs ?? 250;
  const timeoutMs = options?.timeoutMs ?? 5_000;
  const startedAt = Date.now();

  while (!options?.signal?.aborted && Date.now() - startedAt <= timeoutMs) {
    if (track()) {
      return true;
    }

    await sleepWithAbort(intervalMs, options?.signal);
  }

  return false;
}

export async function trackMetaBrowserPageViewWithRetry(options?: {
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}): Promise<boolean> {
  return retryUntilTracked(() => trackMetaBrowserPageView(), options);
}
